import React from "react";

import { GA4EventNames } from "@vahak/core/dist/constants/event-names";
import PlayStoreButton from "@vahak/core/dist/icons/playStoreButton.svg";
import AppStoreButton from "@vahak/core/dist/icons/appStoreButton.svg";

import { IEventTrackerProp } from "../../../types/events";
import { LoadQuoteValues } from "../load-quote";
import DownloadAppAction from "../../common/download-app-qr-modal/DownloadAppAction";
import { COMPACT_APP_DOWNLOAD } from "../compactAppDownload.constants";
import Styles from "./HeroSection.module.scss";
import useHomepageV2Translation from "../hooks/useHomepageV2Translation";
import QuoteFormCard from "./quote-form-card/QuoteFormCard";

type HeroSectionProps = IEventTrackerProp & {
    onOpenCompactPosting: (values: LoadQuoteValues) => void;
    isCompactPostingOpen: boolean;
    isCompactPostingOpening?: boolean;
};

const HeroSection = ({
    trackEvent,
    eventData,
    onOpenCompactPosting,
    isCompactPostingOpen,
    isCompactPostingOpening = false
}: HeroSectionProps) => {
    const { t } = useHomepageV2Translation();

    return (
        <section className={Styles.section}>
            <div className={Styles.container}>
                <div className={Styles.content}>
                    <div className={Styles.left}>
                        <div className={Styles.copy}>
                            <div className={Styles.eyebrow}>
                                <span>{t("homepageV2:hero.tag.region")}</span>
                                <span className={Styles.eyebrowSeparator}>|</span>
                                <span>{t("homepageV2:hero.tag.price")}</span>
                            </div>
                            <h1 className={Styles.title}>{t("homepageV2:hero.title")}</h1>
                            <p className={Styles.subtitle}>{t("homepageV2:hero.subtitle")}</p>
                        </div>
                        <div className={Styles.quoteFormWrap}>
                            <QuoteFormCard
                                trackEvent={trackEvent}
                                eventData={eventData}
                                onOpenCompactPosting={onOpenCompactPosting}
                                isCompactPostingOpen={isCompactPostingOpen}
                                isCompactPostingOpening={isCompactPostingOpening}
                            />
                            <aside className={Styles.appDownload} aria-labelledby="hero-app-download-title">
                                <p id="hero-app-download-title" className={Styles.appDownloadSeparator}>
                                    {t("homepageV2:hero.downloadApp")}
                                </p>
                                <div className={Styles.storeButtons}>
                                    <DownloadAppAction
                                        href={COMPACT_APP_DOWNLOAD.playStoreUrl}
                                        className={Styles.storeButton}
                                        storeUrl={COMPACT_APP_DOWNLOAD.playStoreUrl}
                                        onClick={() => trackEvent?.(GA4EventNames.WEB_V2.download_playstore, eventData)}
                                        aria-label={t("navigation:appDownload.downloadPlayStore")}
                                    >
                                        <PlayStoreButton aria-hidden />
                                    </DownloadAppAction>
                                    <DownloadAppAction
                                        href={COMPACT_APP_DOWNLOAD.appStoreUrl}
                                        className={Styles.storeButton}
                                        storeUrl={COMPACT_APP_DOWNLOAD.appStoreUrl}
                                        onClick={() => trackEvent?.(GA4EventNames.WEB_V2.download_appstore, eventData)}
                                        aria-label={t("navigation:appDownload.downloadAppStore")}
                                    >
                                        <AppStoreButton aria-hidden />
                                    </DownloadAppAction>
                                </div>
                            </aside>
                        </div>
                    </div>

                    <div className={Styles.right}>
                        <div className={Styles.truckFrame}>
                            <img
                                src="/images/homepage-v2/vahak-lcv-hero-cutout.webp"
                                alt={t("homepageV2:hero.truckAlt")}
                                width={866}
                                height={557}
                                draggable={false}
                            />
                            <div className={`${Styles.truckBadge} ${Styles.truckBadgeVerified}`}>
                                <span className={Styles.badgeCheck} aria-hidden>
                                    ✓
                                </span>
                                <span>
                                    <strong>{t("homepageV2:hero.truckBadges.verified")}</strong>
                                    <small>{t("homepageV2:hero.truckBadges.verifiedSubtitle")}</small>
                                </span>
                            </div>
                            <div className={`${Styles.truckBadge} ${Styles.truckBadgeAvailable}`}>
                                <span className={Styles.badgeLive} aria-hidden />
                                <span>
                                    <strong>{t("homepageV2:hero.truckBadges.available")}</strong>
                                    <small>{t("homepageV2:hero.truckBadges.availableSubtitle")}</small>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HeroSection;
